import React, { useState, useEffect, useCallback } from "react";
import API from "../utils/api";

// ── Variable substitution ─────────────────────────────────────
export function applyVariables(text, vars = {}) {
  if (!text) return text;
  return text
    .replace(/\{\{contact_name\}\}/g,      vars.contact_name      || "")
    .replace(/\{\{institution_name\}\}/g,  vars.institution_name  || "")
    .replace(/\{\{sender_name\}\}/g,       vars.sender_name       || "")
    .replace(/\{\{company_name\}\}/g,      vars.company_name      || "");
}

const CATEGORIES = [
  { key: "",                 label: "All Categories" },
  { key: "introduction",    label: "Introduction" },
  { key: "demo_followup",   label: "Demo Follow-up" },
  { key: "clinical_services",label: "Clinical Services" },
  { key: "proposal",        label: "Proposal" },
  { key: "followup_reminder",label: "Follow-up Reminder" },
  { key: "reengagement",    label: "Re-engagement" },
  { key: "thankyou",        label: "Thank You / Closing" },
  { key: "event_announce",  label: "Event / Announcement" },
  { key: "payment_reminder",label: "Payment Reminder" },
  { key: "custom",          label: "Custom" },
];

/**
 * TemplatePicker
 *
 * Props:
 *   type        — "email" | "whatsapp" (required)
 *   onSelect    — fn({ subject, body }) called when user clicks Insert
 *   onClose     — fn() to close the picker
 *   accent      — theme colour string (default #E31E24)
 *   vars        — { contact_name, institution_name, sender_name, company_name }
 *                 for live variable preview
 */
export default function TemplatePicker({ type, onSelect, onClose, accent = "#E31E24", vars = {} }) {
  const [templates, setTemplates]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [category, setCategory]       = useState("");
  const [search, setSearch]           = useState("");
  const [selected, setSelected]       = useState(null);
  const [preview, setPreview]         = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ type });
      if (category) params.set("category", category);
      if (search)   params.set("search", search);
      const res = await API.get(`/message-templates?${params}`);
      setTemplates(res.data || []);
    } catch { setTemplates([]); }
    setLoading(false);
  }, [type, category, search]);

  useEffect(() => { load(); }, [load]);

  const handleInsert = () => {
    if (!selected) return;
    onSelect({
      subject: applyVariables(selected.subject, vars),
      body:    applyVariables(selected.body, vars),
    });
    onClose();
  };

  const filtered = templates; // already filtered server-side

  const overlay = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 9000, padding: "12px",
  };
  const modal = {
    background: "#fff", borderRadius: 18, width: "100%", maxWidth: 640,
    maxHeight: "90vh", display: "flex", flexDirection: "column",
    boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
    overflow: "hidden",
  };
  const hdr = {
    padding: "18px 20px 14px", borderBottom: "1px solid #f1f5f9",
    display: "flex", alignItems: "center", justifyContent: "space-between",
  };

  const TAG_COLOR = {
    email:     { bg: "#e0f2fe", text: "#0369a1" },
    whatsapp:  { bg: "#dcfce7", text: "#15803d" },
  };
  const tc = TAG_COLOR[type] || TAG_COLOR.email;

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal}>
        {/* Header */}
        <div style={hdr}>
          <div>
            <span style={{ fontWeight: 800, fontSize: 15, color: "#1e293b" }}>
              {type === "email" ? "✉️" : "💬"} Choose a Template
            </span>
            <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 700, padding: "2px 9px",
              borderRadius: 20, background: tc.bg, color: tc.text }}>
              {type === "email" ? "Email" : "WhatsApp"}
            </span>
          </div>
          <button onClick={onClose}
            style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8", lineHeight: 1 }}>
            ×
          </button>
        </div>

        {/* Filters */}
        <div style={{ padding: "10px 16px", borderBottom: "1px solid #f1f5f9", display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search templates…"
            style={{ flex: 1, minWidth: 160, padding: "8px 12px", borderRadius: 10,
              border: "1px solid #e2e8f0", fontSize: 13, fontFamily: "inherit",
              outline: "none" }}
          />
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            style={{ padding: "8px 10px", borderRadius: 10, border: "1px solid #e2e8f0",
              fontSize: 12, fontFamily: "inherit", background: "#f8fafc", cursor: "pointer" }}>
            {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>

        {/* Template list / preview */}
        <div style={{ flex: 1, overflowY: "auto", padding: 14 }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 40, color: "#94a3b8", fontSize: 13 }}>Loading templates…</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "#94a3b8", fontSize: 13 }}>
              No templates found.<br />
              <span style={{ fontSize: 12 }}>Ask your admin to seed default templates or create new ones.</span>
            </div>
          ) : preview && selected ? (
            /* Preview pane */
            <div>
              <button onClick={() => setPreview(false)}
                style={{ marginBottom: 12, padding: "6px 14px", background: "#f1f5f9",
                  border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600,
                  cursor: "pointer", fontFamily: "inherit", color: "#64748b" }}>
                ← Back to list
              </button>
              <div style={{ fontWeight: 800, fontSize: 14, color: "#1e293b", marginBottom: 6 }}>
                {selected.name}
              </div>
              {selected.subject && (
                <div style={{ background: "#f8fafc", borderRadius: 8, padding: "8px 12px",
                  marginBottom: 10, fontSize: 12 }}>
                  <strong>Subject:</strong> {applyVariables(selected.subject, vars)}
                </div>
              )}
              <div style={{ whiteSpace: "pre-wrap", fontSize: 13, color: "#334155",
                lineHeight: 1.7, background: "#f8fafc", borderRadius: 10,
                padding: "12px 14px", border: "1px solid #e2e8f0" }}>
                {applyVariables(selected.body, vars)}
              </div>
              {Object.values(vars).some(Boolean) && (
                <div style={{ marginTop: 8, fontSize: 11, color: "#94a3b8" }}>
                  ✓ Variables filled with lead data
                </div>
              )}
            </div>
          ) : (
            /* Card list */
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {filtered.map(t => {
                const isSel = selected?.id === t.id;
                return (
                  <div key={t.id}
                    onClick={() => setSelected(t)}
                    style={{
                      padding: "12px 14px", borderRadius: 12,
                      border: `2px solid ${isSel ? accent : "#e2e8f0"}`,
                      background: isSel ? "#fef2f2" : "#fff",
                      cursor: "pointer", transition: "border-color 0.15s",
                    }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>{t.name}</div>
                        {t.subject && (
                          <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                            Subject: {t.subject}
                          </div>
                        )}
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px",
                        borderRadius: 20, background: "#f1f5f9", color: "#64748b",
                        textTransform: "capitalize", whiteSpace: "nowrap", marginLeft: 8 }}>
                        {(t.category || "").replace(/_/g, " ")}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 5,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {t.body?.substring(0, 90)}…
                    </div>
                    {isSel && (
                      <button
                        onClick={e => { e.stopPropagation(); setPreview(true); }}
                        style={{ marginTop: 6, padding: "4px 10px", background: "#f1f5f9",
                          border: "none", borderRadius: 6, fontSize: 11, fontWeight: 600,
                          cursor: "pointer", fontFamily: "inherit", color: "#475569" }}>
                        Preview
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 16px", borderTop: "1px solid #f1f5f9",
          display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose}
            style={{ padding: "10px 20px", background: "#f1f5f9", border: "none",
              borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer",
              fontFamily: "inherit", color: "#64748b" }}>
            Cancel
          </button>
          <button onClick={handleInsert} disabled={!selected}
            style={{ padding: "10px 24px", background: selected ? accent : "#cbd5e1",
              border: "none", borderRadius: 10, fontWeight: 700, fontSize: 13,
              cursor: selected ? "pointer" : "not-allowed", fontFamily: "inherit", color: "#fff" }}>
            Use Template
          </button>
        </div>
      </div>
    </div>
  );
}
